import { useVisibilityChange } from '@uidotdev/usehooks';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import { audioDB } from '@/lib/storage/audioDB';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';
import { classifyResumeError, nextResumeDecision } from '@/lib/utils/resumeBudget';
import {
  type RecorderInterruptReason,
  recordInterruptionRecovery,
  recordInterruptionStart,
} from '@/lib/utils/interruptionEvents';

interface EncounterSessionContextValue {
  cancelRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<{ blob: Blob; duration: number } | void>;
}

const EncounterSessionContext = createContext<EncounterSessionContextValue | null>(null);

export function EncounterSessionProvider({ children }: { children: ReactNode }) {
  const pauseStartRef = useRef<number>(0);
  // Count of consecutive AUTO-resume failures since the last successful start
  // or successful recovery. Manual resume bypasses + resets this.
  const failedResumeAttemptsRef = useRef<number>(0);

  const { setRecordingMeta } = useEncounterSessionStore();
  const status = useEncounterSessionStore((s) => s.recordingMeta.status);

  const isActiveSession = status === 'recording' || status === 'paused' || status === 'interrupted';
  useScreenWakeLock(isActiveSession);

  const isVisible = useVisibilityChange();

  const handleInterruption = useCallback(
    (reason: RecorderInterruptReason) => {
      recordInterruptionStart(reason);
      const store = useEncounterSessionStore.getState();
      const current = store.recordingMeta.status;
      if (current === 'recording') {
        pauseStartRef.current = Date.now();
        setRecordingMeta({ status: 'interrupted' });
      } else if (current === 'paused') {
        // pauseStartRef already set by pauseRecording — preserve it.
        setRecordingMeta({ status: 'interrupted' });
      }
    },
    [setRecordingMeta],
  );

  const handleError = useCallback((err: unknown) => {
    console.warn('Audio recorder error', err);
  }, []);

  const {
    reacquireMic,
    stop: recorderStop,
    pause: recorderPause,
    start: recorderStart,
    cancel: recorderCancel,
    resume: recorderResume,
  } = useAudioRecorder({ onError: handleError, onInterrupt: handleInterruption });

  const attemptResumeAfterInterruption = useCallback(
    async (opts: { manual: boolean }) => {
      const store = useEncounterSessionStore.getState();
      if (store.recordingMeta.status !== 'interrupted') return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

      if (!opts.manual) {
        const decision = nextResumeDecision({ attempts: failedResumeAttemptsRef.current });
        if (decision.action === 'give-up') {
          recordInterruptionRecovery('budget-exhausted', failedResumeAttemptsRef.current);
          setRecordingMeta({
            lastError: 'Auto-resume failed repeatedly. Tap Resume to try again.',
          });
          return;
        }
      }

      // Optimistic flip closes the window where two concurrent triggers
      // (visibility + manual tap) both pass the guard above.
      const additionalPause = pauseStartRef.current > 0 ? Date.now() - pauseStartRef.current : 0;
      const carryPausedDuration = store.recordingMeta.pausedDuration;
      pauseStartRef.current = 0;
      setRecordingMeta({
        status: 'recording',
        pausedDuration: carryPausedDuration + additionalPause,
      });

      try {
        await reacquireMic();
        const attempt = failedResumeAttemptsRef.current + 1;
        failedResumeAttemptsRef.current = 0;
        setRecordingMeta({ lastError: undefined });
        recordInterruptionRecovery('success', attempt);
      } catch (err) {
        const errorClass = classifyResumeError(err);
        if (errorClass === 'permission-denied') {
          failedResumeAttemptsRef.current = 0;
          pauseStartRef.current = Date.now();
          setRecordingMeta({
            status: 'interrupted',
            pausedDuration: carryPausedDuration,
            lastError: 'Microphone access was denied. Re-enable it in browser settings and tap Resume.',
          });
          recordInterruptionRecovery('permission-denied', 1);
          return;
        }
        failedResumeAttemptsRef.current += 1;
        pauseStartRef.current = Date.now();
        setRecordingMeta({
          status: 'interrupted',
          pausedDuration: carryPausedDuration,
        });
        // Emit a per-attempt failure so the recovery timeline isn't silent
        // between the interruption start and the eventual give-up.
        recordInterruptionRecovery('failure', failedResumeAttemptsRef.current);
        console.warn('Audio auto-resume after interruption failed', err);
      }
    },
    [reacquireMic, setRecordingMeta],
  );

  const startRecording = useCallback(async () => {
    failedResumeAttemptsRef.current = 0;
    await recorderStart();
    setRecordingMeta({
      pausedDuration: 0,
      status: 'recording',
      lastError: undefined,
      startedAt: Date.now(),
      finalDuration: undefined,
    });
  }, [recorderStart, setRecordingMeta]);

  const pauseRecording = useCallback(() => {
    recorderPause();
    pauseStartRef.current = Date.now();
    setRecordingMeta({ status: 'paused' });
  }, [recorderPause, setRecordingMeta]);

  const resumeRecording = useCallback(() => {
    const store = useEncounterSessionStore.getState();
    if (store.recordingMeta.status === 'interrupted') {
      void attemptResumeAfterInterruption({ manual: true });
      return;
    }
    recorderResume();
    const additionalPause = pauseStartRef.current > 0 ? Date.now() - pauseStartRef.current : 0;
    pauseStartRef.current = 0;
    setRecordingMeta({
      status: 'recording',
      pausedDuration: store.recordingMeta.pausedDuration + additionalPause,
    });
  }, [recorderResume, attemptResumeAfterInterruption, setRecordingMeta]);

  const stopRecording = useCallback(async () => {
    const { blob } = await recorderStop();
    const store = useEncounterSessionStore.getState();
    const { startedAt, pausedDuration } = store.recordingMeta;

    let totalPaused = pausedDuration;
    if (pauseStartRef.current > 0) {
      totalPaused += Date.now() - pauseStartRef.current;
      pauseStartRef.current = 0;
    }

    const finalDuration = startedAt ? Date.now() - startedAt - totalPaused : 0;
    await audioDB.save({ blob, duration: finalDuration });
    setRecordingMeta({
      finalDuration,
      status: 'stopped',
      pausedDuration: totalPaused,
    });
    return { blob, duration: finalDuration };
  }, [recorderStop, setRecordingMeta]);

  const cancelRecording = useCallback(() => {
    recorderCancel();
    pauseStartRef.current = 0;
    failedResumeAttemptsRef.current = 0;
    setRecordingMeta({
      status: 'idle',
      startedAt: null,
      pausedDuration: 0,
      lastError: undefined,
      finalDuration: undefined,
    });
  }, [recorderCancel, setRecordingMeta]);

  // Auto-resume on visibility-edge → visible.
  useEffect(() => {
    if (isVisible) {
      void attemptResumeAfterInterruption({ manual: false });
    }
  }, [isVisible, attemptResumeAfterInterruption]);

  // Auto-resume on focus and pageshow — iOS Safari's call UI doesn't always
  // flip document.visibilityState (banner-style call alerts), so we cover
  // the gap with these additional edges.
  useEffect(() => {
    const onFocus = () => void attemptResumeAfterInterruption({ manual: false });
    const onPageShow = () => void attemptResumeAfterInterruption({ manual: false });
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [attemptResumeAfterInterruption]);

  return (
    <EncounterSessionContext.Provider
      value={{
        stopRecording,
        pauseRecording,
        startRecording,
        cancelRecording,
        resumeRecording,
      }}>
      {children}
    </EncounterSessionContext.Provider>
  );
}

export function useEncounterSession() {
  const ctx = useContext(EncounterSessionContext);
  if (!ctx) {
    throw new Error('useEncounterSession must be used within EncounterSessionProvider');
  }
  return ctx;
}
